import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../redux/hook";
import ThemeProviderWrapper from "../theme/ThemeProviderWrapper";
import AuthHeader from "../components/layout/AuthHeader";
// import AuthFooter from "../components/layout/AuthFooter";
import AuthCard from "../components/layout/AuthCard";
import {
  setToken,
  setError,
  clearError,
  setUser,
} from "../redux/Reducers/authSlice";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLoginMutation } from "../redux/api/authApi";
import { useLocation } from "react-router-dom";

const validationSchema = yup.object({
  email: yup
    .string()
    .trim()
    .required("Please enter email")
    .email("Invalid email")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email"),
  password: yup
    .string()
    .trim()
    .required("Please enter password")
    // .matches(
    //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_+\-~()`{}[\]:;"'<>/?.,|\\]).{8,}$/,
    //   "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    // ),
  // rememberMe: yup.boolean(),
});

export default function SignIn() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = React.useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const _role = queryParams.get("role");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: "",
      password: "",
      // rememberMe: false,
    },
  });

  const onSubmit = async (data) => {
    try {
      dispatch(clearError());

      const result = await login({ ...data, role: _role }).unwrap();

      const token = result?.data?.token;
      const user = result?.data?.user;

      // Persist via Redux (sessionStorage — isolated per browser tab)
      dispatch(setUser(user));
      dispatch(setToken(token));

      // ROLE-BASED REDIRECT
      const role = user?.role; // The role comes from the response data.user.role

      if (role === "investor") {
        navigate("/investor/dashboard");
      } else if (role === "assetOwner") {
        // TODO: role & assetOwner role
        navigate("/asset/dashboard");
      } else if (role === "admin") {
        navigate("/admin");
      } else {
        // Fallback route if no valid role
        navigate("/login");
      }
    } catch (error) {
      console.warn(error);
      dispatch(setError("Login failed. Please try again."));
    }
  };

  return (
    <ThemeProviderWrapper>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 50% 20%, rgba(56,189,248,0.10), transparent 60%)," +
              "radial-gradient(circle at 80% 80%, rgba(168,85,247,0.10), transparent 70%)",
            zIndex: -2,
          },
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(0deg, transparent 0, rgba(255,255,255,0.03) 1px)," +
              "linear-gradient(90deg, transparent 0, rgba(255,255,255,0.03) 1px)",
            backgroundSize: "80px 80px",
            opacity: 0.15,
            zIndex: -1,
          },
        }}
      >
        {/* Header */}
        <AuthHeader />

        {/* Card */}
        <AuthCard>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              Welcome Back
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{
                color: "#38bdf8",
                borderColor: "rgba(56,189,248,0.4)",
                borderRadius: "12px",
                textTransform: "none",
                "&:hover": { background: "rgba(56,189,248,0.1)" },
              }}
            >
              {_role === "investor"
                ? "Investor"
                : _role === "assetOwner"
                ? "Asset Owner"
                : "Admin"}
            </Button>
          </Stack>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Sign in to access your dashboard
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2}>
              {/* Email */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
                >
                  Email
                </Typography>
                <TextField
                  type="email"
                  placeholder="you@example.com"
                  fullWidth
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    sx: {
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "10px",
                    },
                  }}
                />
              </Box>

              {/* Password */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
                >
                  Password
                </Typography>
                <TextField
                  type={showPassword ? "text" : "password"}
                  placeholder="********"
                  fullWidth
                  {...register("password")}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    sx: {
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "10px",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword((p) => !p)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ color: "#94a3b8" }} />
                          ) : (
                            <Visibility sx={{ color: "#94a3b8" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 1 }}
            >
              {/* <FormControlLabel
                control={<Checkbox {...register("rememberMe")} />}
                label="Remember me"
              /> */}
              <Link
                onClick={() =>
                  navigate(
                    `/reset-password?role=${encodeURIComponent(_role)}`
                  )
                }
                color="primary"
                underline="hover"
              >
                Forgot password?
              </Link>
            </Stack>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              disabled={isLoading}
              sx={{
                mt: 2,
                py: 1.3,
                fontWeight: 600,
                background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
                borderRadius: "12px",
                textTransform: "none",
              }}
            >
              {isLoading ? "Signing in…" : "Sign In"}
            </Button>
          </Box>

          {_role !== "admin" && (
            <Typography textAlign="center" sx={{ mt: 2 }}>
              Don&apos;t have an account?{" "}
              <Link
                component="button"
                color="primary"
                underline="hover"
                onClick={() =>
                  navigate(_role ? `/signup?role=${_role}` : "/signup")
                }
              >
                Sign Up
              </Link>
            </Typography>
          )}
        </AuthCard>

        {/* Footer */}
        {/* <AuthFooter /> */}
      </Box>
    </ThemeProviderWrapper>
  );
}
